import {
  BankType,
  EmployeeContactType,
  EmployeeHistoryOrderType,
  EmployeeNamePrefixType,
  EmployeeOrderType,
  EmployeeWelfareFundParticipation,
  FinanceChanelType,
  IdentifyPersionType,
  PndType,
  PrompPayType,
} from "@/enums/enums";
import { CalculatedWHT, CreateEmployeeModel, EmployeeModel } from "@/model/employee/employeeModel";
import { FileModel } from "@/model/file/fileModel";
import { SettingDetailModel } from "@/model/setting/settingDetailModel";

const deductionMaxDefault = 100000;

export function GetCreateEmployeeTemplate(): CreateEmployeeModel {
  return {
    id: 0,
    code: {
      value: "",
      validateError: false,
    },
    file: {
      fileName: "",
      fileType: "",
      rawString: "",
    },
    fileEmployeeIDCard: {
      fileName: "",
      fileType: "",
      rawString: "",
    },
    fileEmployeeContract: {
      fileName: "",
      fileType: "",
      rawString: "",
    },
    prefixName: { value: EmployeeNamePrefixType.Mr },
    prefixNameOther: {
      value: "",
      validateError: false,
    },
    firstNameTH: {
      value: "",
      validateError: false,
    },
    lastNameTH: {
      value: "",
      validateError: false,
    },
    firstNameEN: {
      value: "",
      validateError: false,
    },
    lastNameEN: {
      value: "",
      validateError: false,
    },
    nationality: {
      value: {
        code: "TH",
      },
      validateError: false,
    },
    birthDate: {
      value: null,
      validateError: false,
    },
    personalIDCodeType: IdentifyPersionType.CitizenId,
    citizenIDCode: {
      value: "",
      validateError: false,
    },
    department: {
      value: { id: 0 },
      validateError: false,
    },
    contractType: { value: EmployeeContactType.Monthly },
    position: {
      value: { id: 0 },
      validateError: false,
    },
    email: {
      value: "",
      validateError: false,
    },
    mobile: {
      value: "",
      validateError: false,
    },
    emergencyContact: {
      value: "",
      validateError: false,
    },
    emergencyMobile: {
      value: "",
      validateError: false,
    },
    address: {
      value: "",
      validateError: false,
    },
    subDistrict: {
      value: "",
      validateError: false,
    },
    city: {
      value: "",
      validateError: false,
    },
    province: {
      value: "",
      validateError: false,
    },
    postalCode: {
      value: "",
      validateError: false,
    },
    startDate: {
      value: new Date(),
      validateError: false,
    },
    //employeeCompensation
    defaultSalary: {
      value: "0.00",
      validateError: false,
    },
    salaryAdjustmentAccount: { id: -1 },
    registerSSO: true,
    isRegisterEWF: true,
    wht: true,
    whtType: { value: PndType.PND1kor },
    defaultWHT: {
      value: "0.00",
      validateError: false,
    },
    defaultWHT402: {
      value: "0.00",
      validateError: false,
    },
    //employeeFinance
    financeChannelType: { value: FinanceChanelType.Bank },
    bankTypeId: 0,
    bankAccountType: { value: BankType.SavingAccount },
    bankAccountName: {
      value: "",
      validateError: false,
    },
    bankAccountNumber: {
      value: "",
      validateError: false,
    },
    bankAccountBranchName: {
      value: "",
      validateError: false,
    },
    bankAccountBranchNumber: {
      value: "",
      validateError: false,
    },
    promptPayType: { value: PrompPayType.CitizenId },
    promptPayIDNumber: {
      value: "",
      validateError: false,
    },
    //employeeAllowance
    defaultAllowancePersonal: {
      value: "60,000.00",
      validateError: false,
    },
    defaultAllowanceSpouse: {
      value: "0.00",
      validateError: false,
    },
    defaultAllowancePersonCare: {
      value: "0.00",
      validateError: false,
    },
    defaultAllowanceChild: {
      value: "0.00",
      validateError: false,
    },
    defaultAllowanceLifeInsurance: {
      value: "0.00",
      validateError: false,
    },
    defaultAllowanceHealthInsurance: {
      value: "0.00",
      validateError: false,
    },
    defaultAllowanceFundContribution: {
      value: "0.00",
      validateError: false,
    },
    defaultAllowanceSocialSecurityContributions: {
      value: "9000.00",
      validateError: false,
    },
    defaultAllowanceFund: {
      value: "0.00",
      validateError: false,
    },
    defaultAllowanceOther: {
      value: "0.00",
      validateError: false,
    },
  };
}

export function ValidateEmployeeTemplate(employeeTemplate: any, isImport = false) {
  let codeValidateError = false;

  if (isImport) {
    codeValidateError = employeeTemplate.code.validateError;
  } else {
    codeValidateError = employeeTemplate.code.value == "" || employeeTemplate.code.validateError;
  }

  let prefixNameOtherValidateError = false;

  if (employeeTemplate.prefixName.value === EmployeeNamePrefixType.Other) {
    prefixNameOtherValidateError =
      employeeTemplate.prefixNameOther.value == "" || employeeTemplate.prefixNameOther.validateError;
  }

  const firstNameTHValidateError =
    employeeTemplate.firstNameTH.value == "" || employeeTemplate.firstNameTH.validateError;

  const lastNameTHValidateError = employeeTemplate.lastNameTH.value == "" || employeeTemplate.lastNameTH.validateError;

  const firstNameENValidateError = employeeTemplate.firstNameEN.validateError;

  const lastNameENValidateError = employeeTemplate.lastNameEN.validateError;

  let citizenIDCodeValidateError = false;
  employeeTemplate.citizenIDCode.value = employeeTemplate.citizenIDCode.value.trim();
  if (employeeTemplate.personalIDCodeType == IdentifyPersionType.CitizenId) {
    if (employeeTemplate.citizenIDCode.value.length == 13) {
      const reg = new RegExp("^[0-9]+$");
      const regNumberOnly = reg.test(employeeTemplate.citizenIDCode.value);
      citizenIDCodeValidateError = regNumberOnly ? employeeTemplate.citizenIDCode.validateError : true;
    } else {
      citizenIDCodeValidateError = true;
    }
  } else {
    citizenIDCodeValidateError =
      employeeTemplate.citizenIDCode.value == "" || employeeTemplate.citizenIDCode.validateError;
  }

  const departmentValidateError =
    !(employeeTemplate.department.value && employeeTemplate.department.value.id > 0) ||
    employeeTemplate.department.validateError;
  const positionValidateError =
    !(employeeTemplate.position.value && employeeTemplate.position.value.id > 0) ||
    employeeTemplate.position.validateError;

  const emailValidateError = employeeTemplate.email.validateError;

  const mobileValidateError = employeeTemplate.mobile.validateError;

  const emergencyContactValidateError = employeeTemplate.emergencyContact.validateError;

  const emergencyMobileValidateError = employeeTemplate.emergencyMobile.validateError;

  const addressValidateError = employeeTemplate.address.validateError;

  const subDistrictValidateError = employeeTemplate.subDistrict.validateError;

  const cityValidateError = employeeTemplate.city.validateError;

  const provinceValidateError = employeeTemplate.province.validateError;

  const postalCodeValidateError = employeeTemplate.postalCode.validateError;

  let startDateValidateError = false;

  if (isImport) {
    startDateValidateError =
      employeeTemplate.startDate.value === "NaN-aN-aN" || employeeTemplate.startDate.validateError;
  } else {
    startDateValidateError = employeeTemplate.startDate.validateError;
  }

  const defaultSalaryValidateError = employeeTemplate.defaultSalary.validateError;

  const defaultWHTValidateError = employeeTemplate.defaultWHT.validateError;

  const defaultAllowancePersonal = employeeTemplate.defaultAllowancePersonal.validateError;

  const defaultAllowanceSpouse = employeeTemplate.defaultAllowanceSpouse.validateError;

  const defaultAllowancePersonCare = employeeTemplate.defaultAllowancePersonCare.validateError;

  const defaultAllowanceChild = employeeTemplate.defaultAllowanceChild.validateError;

  const defaultAllowanceLifeInsurance = employeeTemplate.defaultAllowanceLifeInsurance.validateError;

  const defaultAllowanceHealthInsurance = employeeTemplate.defaultAllowanceHealthInsurance.validateError;

  const defaultAllowanceFundContribution = employeeTemplate.defaultAllowanceFundContribution.validateError;

  const defaultWHT402ValidateError = employeeTemplate.defaultWHT402.validateError;

  const defaultAllowanceSocialSecurityContributions =
    employeeTemplate.defaultAllowanceSocialSecurityContributions.validateError;
  const defaultAllowanceFund = employeeTemplate.defaultAllowanceSocialSecurityContributions.validateError;
  const defaultAllowanceOther = employeeTemplate.defaultAllowanceSocialSecurityContributions.validateError;

  let bankAccountNameValidateError = false;

  let bankAccountNumberValidateError = false;

  let bankAccountBranchNameValidateError = false;

  let bankAccountBranchNumberValidateError = false;

  let promptPayIDNumberValidateError = false;

  if (employeeTemplate.financeChannelType.value === FinanceChanelType.Bank) {
    bankAccountNameValidateError = employeeTemplate.bankAccountName.validateError;
    const isNumberOnly = /^\d+$/.test(employeeTemplate.bankAccountNumber.value);
    bankAccountNumberValidateError =
      employeeTemplate.bankAccountNumber.value == "" ||
      employeeTemplate.bankAccountNumber.validateError ||
      !isNumberOnly;

    bankAccountBranchNameValidateError = employeeTemplate.bankAccountBranchName.validateError;

    bankAccountBranchNumberValidateError = employeeTemplate.bankAccountBranchNumber.validateError;
  } else if (employeeTemplate.financeChannelType.value === FinanceChanelType.PrompPay) {
    promptPayIDNumberValidateError = employeeTemplate.promptPayIDNumber.validateError;
  }

  if (
    !codeValidateError &&
    !prefixNameOtherValidateError &&
    !firstNameTHValidateError &&
    !lastNameTHValidateError &&
    !firstNameENValidateError &&
    !lastNameENValidateError &&
    !citizenIDCodeValidateError &&
    !departmentValidateError &&
    !positionValidateError &&
    !emailValidateError &&
    !mobileValidateError &&
    !emergencyContactValidateError &&
    !emergencyMobileValidateError &&
    !addressValidateError &&
    !subDistrictValidateError &&
    !cityValidateError &&
    !provinceValidateError &&
    !postalCodeValidateError &&
    !startDateValidateError &&
    !defaultSalaryValidateError &&
    !defaultWHTValidateError &&
    !bankAccountNameValidateError &&
    !bankAccountNumberValidateError &&
    !bankAccountBranchNameValidateError &&
    !bankAccountBranchNumberValidateError &&
    !promptPayIDNumberValidateError &&
    !defaultAllowancePersonal &&
    !defaultAllowanceSpouse &&
    !defaultAllowancePersonCare &&
    !defaultAllowanceChild &&
    !defaultAllowanceLifeInsurance &&
    !defaultAllowanceHealthInsurance &&
    !defaultAllowanceFundContribution &&
    !defaultAllowanceSocialSecurityContributions &&
    !defaultAllowanceFund &&
    !defaultAllowanceOther &&
    !defaultWHT402ValidateError
  ) {
    return true;
  } else {
    employeeTemplate.code.validateError = codeValidateError;
    employeeTemplate.prefixNameOther.validateError = prefixNameOtherValidateError;
    employeeTemplate.firstNameTH.validateError = firstNameTHValidateError;
    employeeTemplate.lastNameTH.validateError = lastNameTHValidateError;
    employeeTemplate.firstNameEN.validateError = firstNameENValidateError;
    employeeTemplate.lastNameEN.validateError = lastNameENValidateError;
    employeeTemplate.citizenIDCode.validateError = citizenIDCodeValidateError;
    employeeTemplate.department.validateError = departmentValidateError;
    employeeTemplate.position.validateError = positionValidateError;
    employeeTemplate.email.validateError = emailValidateError;
    employeeTemplate.mobile.validateError = mobileValidateError;
    employeeTemplate.emergencyContact.validateError = emergencyContactValidateError;
    employeeTemplate.emergencyMobile.validateError = emergencyMobileValidateError;
    employeeTemplate.address.validateError = addressValidateError;
    employeeTemplate.subDistrict.validateError = subDistrictValidateError;
    employeeTemplate.city.validateError = cityValidateError;
    employeeTemplate.province.validateError = provinceValidateError;
    employeeTemplate.postalCode.validateError = postalCodeValidateError;
    employeeTemplate.startDate.validateError = startDateValidateError;
    employeeTemplate.defaultSalary.validateError = defaultSalaryValidateError;
    employeeTemplate.defaultWHT.validateError = defaultWHTValidateError;
    employeeTemplate.defaultWHT402.validateError = defaultWHT402ValidateError;
    employeeTemplate.bankAccountName.validateError = bankAccountNameValidateError;
    employeeTemplate.bankAccountNumber.validateError = bankAccountNumberValidateError;
    employeeTemplate.bankAccountBranchName.validateError = bankAccountBranchNameValidateError;
    employeeTemplate.bankAccountBranchNumber.validateError = bankAccountBranchNumberValidateError;
    employeeTemplate.promptPayIDNumber.validateError = promptPayIDNumberValidateError;
    employeeTemplate.defaultAllowancePersonal.validateError = defaultSalaryValidateError;
    employeeTemplate.defaultAllowanceSpouse.validateError = defaultAllowanceSpouse;
    employeeTemplate.defaultAllowancePersonCare.validateError = defaultAllowancePersonCare;
    employeeTemplate.defaultAllowanceChild.validateError = defaultAllowanceChild;
    employeeTemplate.defaultAllowanceLifeInsurance.validateError = defaultAllowanceLifeInsurance;
    employeeTemplate.defaultAllowanceHealthInsurance.validateError = defaultAllowanceHealthInsurance;
    employeeTemplate.defaultAllowanceFundContribution.validateError = defaultAllowanceFundContribution;
    employeeTemplate.defaultAllowanceSocialSecurityContributions.validateError =
      defaultAllowanceSocialSecurityContributions;
    employeeTemplate.defaultAllowanceFund.validateError = defaultAllowanceFund;
    employeeTemplate.defaultAllowanceOther.validateError = defaultAllowanceOther;
    return false;
  }
}
export function ToEmployeeModel(employeeTemplate: any, isImport = false, defaultIsRegisterEWF = false): EmployeeModel {
  const employeeModel: EmployeeModel = {
    id: employeeTemplate.id,
    departmentId: employeeTemplate.department.value.id ? employeeTemplate.department.value.id : 0,
    positionId: employeeTemplate.position.value.id ? employeeTemplate.position.value.id : 0,
    code: employeeTemplate.code.value,
    profileImageUrl: "",
    namePrefixType: employeeTemplate.prefixName.value,
    namePrefixName:
      employeeTemplate.prefixName.value == EmployeeNamePrefixType.Other ? employeeTemplate.prefixNameOther.value : "",
    firstNameTh: employeeTemplate.firstNameTH.value,
    firstNameEn: employeeTemplate.firstNameEN.value,
    lastNameTh: employeeTemplate.lastNameTH.value,
    lastNameEn: employeeTemplate.lastNameEN.value,
    identifyPersionType: employeeTemplate.personalIDCodeType,
    citizenIdCode:
      employeeTemplate.personalIDCodeType == IdentifyPersionType.CitizenId ? employeeTemplate.citizenIDCode.value : "",
    passportIdCode:
      employeeTemplate.personalIDCodeType == IdentifyPersionType.Passport ? employeeTemplate.citizenIDCode.value : "",
    email: employeeTemplate.email.value,
    phone: employeeTemplate.mobile.value,
    emergencyContactName: employeeTemplate.emergencyContact.value,
    emergencyContactPhone: employeeTemplate.emergencyMobile.value,
    address: employeeTemplate.address.value,
    subDistrict: employeeTemplate.subDistrict.value,
    city: employeeTemplate.city.value,
    province: employeeTemplate.province.value,
    postCode: employeeTemplate.postalCode.value,
    employeeStartDate: employeeTemplate.startDate.value,
    salaryTotal: employeeTemplate.defaultSalary.value.ToNumber(),
    isRegisterSSO: isImport ? employeeTemplate.registerSSO.value == 0 : employeeTemplate.registerSSO,
    isWht: employeeTemplate.wht,
    pndType: employeeTemplate.whtType.value,
    whtAmount: employeeTemplate.defaultWHT.value.ToNumber(),
    wht402Amount: employeeTemplate.defaultWHT402.value.ToNumber(),
    prompPayNumber:
      employeeTemplate.financeChannelType.value === FinanceChanelType.PrompPay
        ? employeeTemplate.promptPayIDNumber.value
        : "",
    prompPayType:
      employeeTemplate.financeChannelType.value === FinanceChanelType.PrompPay
        ? employeeTemplate.promptPayType.value
        : 0,
    bankAccountBranchNumber:
      employeeTemplate.financeChannelType.value === FinanceChanelType.Bank
        ? employeeTemplate.bankAccountBranchNumber.value
        : "",
    bankAccountBranchName:
      employeeTemplate.financeChannelType.value === FinanceChanelType.Bank
        ? employeeTemplate.bankAccountBranchName.value
        : "",
    bankAccountNumber:
      employeeTemplate.financeChannelType.value === FinanceChanelType.Bank
        ? employeeTemplate.bankAccountNumber.value
        : "",
    bankAccountName:
      employeeTemplate.financeChannelType.value === FinanceChanelType.Bank
        ? employeeTemplate.bankAccountName.value
        : "",
    bankType:
      employeeTemplate.financeChannelType.value === FinanceChanelType.Bank ? employeeTemplate.bankAccountType.value : 0,
    bankReferenceId:
      employeeTemplate.financeChannelType.value === FinanceChanelType.Bank ? employeeTemplate.bankTypeId : 0,
    financeChanelType: employeeTemplate.financeChannelType.value,
    accountId: employeeTemplate.salaryAdjustmentAccount.id == -1 ? null : employeeTemplate.salaryAdjustmentAccount.id,
    contactType: employeeTemplate.contractType.value,
    allowancePersonal: employeeTemplate.defaultAllowancePersonal.value.ToNumber(),
    allowanceSpouse: employeeTemplate.defaultAllowanceSpouse.value.ToNumber(),
    allowancePersonCare: employeeTemplate.defaultAllowancePersonCare.value.ToNumber(),
    allowanceChild: employeeTemplate.defaultAllowanceChild.value.ToNumber(),
    allowanceLifeInsurance: employeeTemplate.defaultAllowanceLifeInsurance.value.ToNumber(),
    allowanceHealthInsurance: employeeTemplate.defaultAllowanceHealthInsurance.value.ToNumber(),
    allowanceFundContribution: employeeTemplate.defaultAllowanceFundContribution.value.ToNumber(),
    allowanceSocialSecurityContributions: employeeTemplate.defaultAllowanceSocialSecurityContributions.value.ToNumber(),
    allowanceFund: employeeTemplate.defaultAllowanceFund.value.ToNumber(),
    allowanceOther: employeeTemplate.defaultAllowanceOther.value.ToNumber(),
    nationality: isImport ? "TH" : employeeTemplate.nationality.value.code,
    birthDate: isImport ? null : (employeeTemplate.birthDate.value?.ToDateJsonString() ?? null),
    isRegisterEWF: isImport ? defaultIsRegisterEWF : employeeTemplate.isRegisterEWF,
  };

  return employeeModel;
}

export function ConvertToImportEmployee(employeeRawList: any, settingDetail: SettingDetailModel): any {
  const employeeList: any = [];

  if (employeeRawList && employeeRawList.length > 0) {
    const defaultIsRegisterEWF: boolean = [
      EmployeeWelfareFundParticipation.MandatoryParticipation,
      EmployeeWelfareFundParticipation.VoluntaryParticipation,
    ].includes(settingDetail.employeeWelfareFundParticipation);

    for (const employeeRaw of employeeRawList) {
      employeeList.push(ToEmployeeModel(employeeRaw, true, defaultIsRegisterEWF));
    }
  }

  return employeeList;
}
export function ToEmployeeFileModel(employeeTemplate: any) {
  const employeeFileModel: FileModel = {
    rawString: employeeTemplate.file.rawString,
    fileName: employeeTemplate.file.fileName,
    contentType: employeeTemplate.file.fileType,
    fileUr: "",
  };

  return employeeFileModel;
}

export function ToFileEmployeeIDCardModel(employeeTemplate: any) {
  const employeeIDCard: FileModel = {
    rawString: employeeTemplate.fileEmployeeIDCard.rawString,
    fileName: employeeTemplate.fileEmployeeIDCard.fileName,
    contentType: employeeTemplate.fileEmployeeIDCard.fileType,
    fileUr: "",
  };
  return employeeIDCard;
}

export function ToFileEmployeeContractModel(employeeTemplate: any) {
  const employeeContract: FileModel = {
    rawString: employeeTemplate.fileEmployeeContract.rawString,
    fileName: employeeTemplate.fileEmployeeContract.fileName,
    contentType: employeeTemplate.fileEmployeeContract.fileType,
    fileUr: "",
  };
  return employeeContract;
}

export function SetTapsEmployeeTable(tableCount: number) {
  return [
    {
      name: "Table_Tap_All",
      length: tableCount,
      bgLengthColor: "gray",
      showtableCount: false,
      tableViewStatusType: 0,
      active: true,
    },
  ];
}

export function SetHeaderEmployeeTable(tableViewModel: any) {
  const header = [
    {
      name: "check",
      hidden: true,
      checkAll: false,
    },
    {
      name: "",
      sortBy: null,
      sortingType: null,

      align: "flex-start",
    },
    {
      name: "Employee_Code",
      sortBy: EmployeeOrderType.Code,
      sortingType: null,
      align: "flex-start",
    },
    {
      name: "Name",
      sortBy: EmployeeOrderType.Name,
      sortingType: null,
      width: 100,
      align: "flex-start",
    },
    {
      name: "Department",
      sortBy: EmployeeOrderType.Department,
      sortingType: null,
      width: 5,
      align: "flex-start",
    },
    {
      name: "Position",
      sortBy: null,
      sortingType: null,
      width: 5,
      align: "flex-start",
    },
    {
      name: "Salary",
      sortBy: EmployeeOrderType.Salary,
      sortingType: null,
      align: "flex-end",
    },
    {
      name: "",
      sortBy: null,
      sortingType: null,
      align: "flex-start",
    },
    {
      name: "",
      sortBy: null,
      sortingType: null,
      align: "flex-start",
    },
  ];

  header.forEach((item) => {
    if (item.sortBy === tableViewModel.orderType) {
      item.sortingType = tableViewModel.sortingType;
    }
  });

  return header;
}

export function GetEditEmployeeTemplate(employeeDetail: EmployeeModel): CreateEmployeeModel {
  return {
    id: employeeDetail.id,
    code: {
      value: employeeDetail.code,
      validateError: false,
    },
    file: {
      fileName: "",
      fileType: "",
      rawString: "",
    },
    fileEmployeeIDCard: {
      fileName: "",
      fileType: "",
      rawString: "",
    },
    fileEmployeeContract: {
      fileName: "",
      fileType: "",
      rawString: "",
    },
    prefixName: { value: employeeDetail.namePrefixType },
    prefixNameOther: {
      value: employeeDetail.namePrefixName,
      validateError: false,
    },
    firstNameTH: {
      value: employeeDetail.firstNameTh,
      validateError: false,
    },
    lastNameTH: {
      value: employeeDetail.lastNameTh,
      validateError: false,
    },
    firstNameEN: {
      value: employeeDetail.firstNameEn,
      validateError: false,
    },
    lastNameEN: {
      value: employeeDetail.lastNameEn,
      validateError: false,
    },
    nationality: {
      value: {
        code: employeeDetail.nationality,
      },
      validateError: false,
    },
    birthDate: {
      value: employeeDetail.birthDate === null ? null : new Date(employeeDetail.birthDate),
      validateError: false,
    },
    personalIDCodeType: employeeDetail.identifyPersionType,
    citizenIDCode: {
      value:
        employeeDetail.identifyPersionType == IdentifyPersionType.CitizenId
          ? employeeDetail.citizenIdCode
          : employeeDetail.passportIdCode,
      validateError: false,
    },
    department: { value: { id: employeeDetail.departmentId }, validateError: false },
    contractType: { value: employeeDetail.contactType },
    position: { value: { id: employeeDetail.positionId }, validateError: false },
    email: {
      value: employeeDetail.email,
      validateError: false,
    },
    mobile: {
      value: employeeDetail.phone,
      validateError: false,
    },
    emergencyContact: {
      value: employeeDetail.emergencyContactName,
      validateError: false,
    },
    emergencyMobile: {
      value: employeeDetail.emergencyContactPhone,
      validateError: false,
    },
    address: {
      value: employeeDetail.address,
      validateError: false,
    },
    subDistrict: {
      value: employeeDetail.subDistrict,
      validateError: false,
    },
    city: {
      value: employeeDetail.city,
      validateError: false,
    },
    province: {
      value: employeeDetail.province,
      validateError: false,
    },
    postalCode: {
      value: employeeDetail.postCode,
      validateError: false,
    },
    startDate: {
      value: new Date(employeeDetail.employeeStartDate),
      validateError: false,
    },
    //employeeCompensation
    defaultSalary: {
      value: employeeDetail.salaryTotal.FormatMoney(),
      validateError: false,
    },
    salaryAdjustmentAccount: { id: employeeDetail.accountId ? employeeDetail.accountId : -1 },
    registerSSO: employeeDetail.isRegisterSSO,
    isRegisterEWF: employeeDetail.isRegisterEWF,
    wht: employeeDetail.isWht,
    whtType: { value: employeeDetail.pndType },
    defaultWHT: {
      value: employeeDetail.whtAmount.FormatMoney(),
      validateError: false,
    },
    defaultWHT402: {
      value: employeeDetail.wht402Amount.FormatMoney(),
      validateError: false,
    },
    //employeeFinance
    financeChannelType: { value: employeeDetail.financeChanelType },
    bankTypeId: employeeDetail.bankReferenceId,
    bankAccountType: { value: employeeDetail.bankType },
    bankAccountName: {
      value: employeeDetail.bankAccountName,
      validateError: false,
    },
    bankAccountNumber: {
      value: employeeDetail.bankAccountNumber,
      validateError: false,
    },
    bankAccountBranchName: {
      value: employeeDetail.bankAccountBranchName,
      validateError: false,
    },
    bankAccountBranchNumber: {
      value: employeeDetail.bankAccountBranchNumber,
      validateError: false,
    },
    promptPayType: { value: employeeDetail.prompPayType },
    promptPayIDNumber: {
      value: employeeDetail.prompPayNumber,
      validateError: false,
    },
    //employeeAllowance
    defaultAllowancePersonal: {
      value: employeeDetail.allowancePersonal.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceSpouse: {
      value: employeeDetail.allowanceSpouse.FormatMoney(),
      validateError: false,
    },
    defaultAllowancePersonCare: {
      value: employeeDetail.allowancePersonCare.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceChild: {
      value: employeeDetail.allowanceChild.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceLifeInsurance: {
      value: employeeDetail.allowanceLifeInsurance.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceHealthInsurance: {
      value: employeeDetail.allowanceHealthInsurance.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceFundContribution: {
      value: employeeDetail.allowanceFundContribution.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceSocialSecurityContributions: {
      value: employeeDetail.allowanceSocialSecurityContributions.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceFund: {
      value: employeeDetail.allowanceFund.FormatMoney(),
      validateError: false,
    },
    defaultAllowanceOther: {
      value: employeeDetail.allowanceOther.FormatMoney(),
      validateError: false,
    },
  };
}
export function SetTapsEmployeeHistoryTable(tableCount: number) {
  return [
    {
      name: "Table_Tap_All",
      length: tableCount,
      bgLengthColor: "gray",
      showtableCount: false,
      tableViewStatusType: 0,
      active: true,
    },
  ];
}

export function SetHeaderEmployeeHistoryTable(tableViewModel: any) {
  const header = [
    {
      name: "check",
      hidden: true,
      checkAll: false,
    },
    {
      name: "Pay_Run_Code",
      sortBy: null,
      width: 20,
      sortingType: null,
      align: "flex-start",
    },
    {
      name: "Issed_Date",
      width: 20,
      sortBy: EmployeeHistoryOrderType.Date,
      sortingType: null,
      align: "flex-start",
    },
    {
      name: "Salary",
      sortBy: null,
      sortingType: null,
      align: "flex-end",
    },
    {
      name: "Social_Security",
      width: 20,
      sortBy: null,
      sortingType: null,
      align: "flex-end",
    },
    {
      name: "Withholding_tax",
      width: 30,
      sortBy: null,
      sortingType: null,
      align: "flex-end",
    },
    {
      name: "Total_Revenue",
      sortBy: null,
      width: 20,
      sortingType: null,
      align: "flex-end",
    },
    {
      name: "Total_Deduct",
      width: 20,
      sortBy: null,
      sortingType: null,
      align: "flex-end",
    },
    {
      name: "Net_Salary",
      sortBy: null,
      sortingType: null,
      align: "flex-end",
    },
    {
      name: "",
      width: 10,
      sortBy: null,
      sortingType: null,
      align: "flex-start",
    },
  ];
  header.forEach((item) => {
    if (item.sortBy === tableViewModel.sortBy) {
      item.sortingType = tableViewModel.sortType;
    } else {
      item.sortingType = null;
    }
  });
  return header;
}

function GetPersonalIncomeTax(_incomeNet: number) {
  let incomeNet = _incomeNet - 150000;
  let personalIncomeTax = 0;
  //150,001 – 300,000 150,000 5 %
  if (incomeNet > 150000) {
    personalIncomeTax = (150000 * 5) / 100;
    incomeNet = _incomeNet - 300000;

    //300,001 – 500,000 200,000 10 %
    if (incomeNet > 200000) {
      personalIncomeTax += (200000 * 10) / 100;
      incomeNet = _incomeNet - 500000;

      //500,001 – 750,000 250,000 15 %
      if (incomeNet > 250000) {
        personalIncomeTax += (250000 * 15) / 100;
        incomeNet = _incomeNet - 750000;

        //750,001 – 1,000,000 250,000 20 %
        if (incomeNet > 250000) {
          personalIncomeTax += (250000 * 20) / 100;
          incomeNet = _incomeNet - 1000000;

          //1,000,001 – 2,000,000 1,000,000 25 %
          if (incomeNet > 1000000) {
            personalIncomeTax += (1000000 * 25) / 100;
            incomeNet = _incomeNet - 2000000;

            //2,000,001 – 5,000,000 3,000,000 30 %
            if (incomeNet > 3000000) {
              personalIncomeTax += (3000000 * 30) / 100;
              incomeNet = _incomeNet - 5000000;

              //5,000,001 บาทขึ้นไป ไม่จํากัด 35 %
              if (incomeNet > 0) {
                personalIncomeTax += (incomeNet * 35) / 100;
              }
            } else if (incomeNet > 0) {
              personalIncomeTax += (incomeNet * 30) / 100;
            }
          } else if (incomeNet > 0) {
            personalIncomeTax += (incomeNet * 25) / 100;
          }
        } else if (incomeNet > 0) {
          personalIncomeTax += (incomeNet * 20) / 100;
        }
      } else if (incomeNet > 0) {
        personalIncomeTax += (incomeNet * 15) / 100;
      }
    } else if (incomeNet > 0) {
      personalIncomeTax += (incomeNet * 10) / 100;
    }
  } else if (incomeNet > 0) {
    personalIncomeTax += (incomeNet * 5) / 100;
  }

  return personalIncomeTax;
}
function GetSummaryTotalTaxAmount(totalTax: number[]): number {
  if (!totalTax) {
    return 0;
  } else {
    return totalTax.reduce((sum, amount) => sum + amount, 0);
  }
}

function AutoCalculateTaxEmployee(netIncome: number): number[] {
  const taxAmounts: number[] = [];
  const brackets = [
    { min: 0, max: 150000, rate: 0, width: 150000 },
    { min: 150001, max: 300000, rate: 5, width: 150000 },
    { min: 300001, max: 500000, rate: 10, width: 200000 },
    { min: 500001, max: 750000, rate: 15, width: 250000 },
    { min: 750001, max: 1000000, rate: 20, width: 250000 },
    { min: 1000001, max: 2000000, rate: 25, width: 1000000 },
    { min: 2000001, max: 5000000, rate: 30, width: 3000000 },
    { min: 5000001, max: null, rate: 35, width: null },
  ];

  let remainingIncome = netIncome > 150000 ? netIncome - 150000 : 0;

  for (const bracket of brackets) {
    let taxableAmount = 0;

    if (bracket.rate === 0) {
      taxAmounts.push(0);
      continue;
    }

    if (remainingIncome > 0) {
      if (bracket.width === null) {
        taxableAmount = (remainingIncome * bracket.rate) / 100;
      } else if (remainingIncome > bracket.width) {
        taxableAmount = (bracket.width * bracket.rate) / 100;
        remainingIncome -= bracket.width;
      } else {
        taxableAmount = (remainingIncome * bracket.rate) / 100;
        remainingIncome = 0;
      }
    }
    taxAmounts.push(taxableAmount.FormatMoney().ToNumber());
  }
  return taxAmounts;
}
export function AutoCalculateWhtEmployee(salary: number, wht402Amount: number, totalAllowance: number): CalculatedWHT {
  const deductionMax = deductionMaxDefault;
  const income401 = salary.ToNumber() * 12;
  const income402 = wht402Amount.ToNumber();
  const incomeTotal = income401 + income402;
  let deduction = (incomeTotal * 50) / 100;
  deduction = deduction > deductionMax ? deductionMax : deduction;
  const netIncomeAfterExpense = incomeTotal - deduction;
  let incomeNet = netIncomeAfterExpense - totalAllowance;
  incomeNet = incomeNet > 0 ? incomeNet : 0;
  const personalIncomeTax = GetPersonalIncomeTax(incomeNet);
  const taxTotal: number[] = AutoCalculateTaxEmployee(incomeNet);
  const summaryTaxTotal: number = GetSummaryTotalTaxAmount(taxTotal);

  return {
    income401: income401,
    income402: income402,
    deduction: deduction,
    incomeNet: incomeNet,
    personalIncomeTax: personalIncomeTax,
    personalIncomeTaxPerMount: personalIncomeTax / 12,
    incomeTotal: incomeTotal,
    netIncomeAfterExpense: netIncomeAfterExpense,
    taxTotal: taxTotal,
    summaryTaxTotal: summaryTaxTotal,
  };
}

export function AutoCalculateWhtSalaryCreate(
  salary: number,
  taxCalculatedAmount: number,
  month: number,
  revenueOther: number,
  deductOther: number,
  revenueOther402: number,
  deductOther402: number,
  whtTotal: number,
  totalAllowance: number
) {
  const deductionMax = deductionMaxDefault;
  const income401 = taxCalculatedAmount + salary * (12 - month) + revenueOther - deductOther;
  const income402 = revenueOther402 - deductOther402;
  const incomeTotal = income401 + income402;
  let deduction = (incomeTotal * 50) / 100;
  deduction = deduction > deductionMax ? deductionMax : deduction;
  const netIncomeAfterExpense = incomeTotal - deduction;

  let incomeNet = netIncomeAfterExpense - totalAllowance;

  incomeNet = incomeNet > 0 ? incomeNet : 0;
  incomeNet = incomeNet > 0 ? incomeNet : 0;
  const personalIncomeTax = GetPersonalIncomeTax(incomeNet);
  const taxTotal: number[] = AutoCalculateTaxEmployee(incomeNet);
  const summaryTaxTotal: number = GetSummaryTotalTaxAmount(taxTotal);
  let personalIncomeTaxPerMount = (personalIncomeTax - whtTotal) / (12 - month);
  personalIncomeTaxPerMount = personalIncomeTaxPerMount > 0 ? personalIncomeTaxPerMount : 0;

  return {
    income401: income401,
    income402: income402,
    deduction: deduction,
    incomeNet: incomeNet,
    personalIncomeTax: personalIncomeTax,
    personalIncomeTaxPerMount: personalIncomeTaxPerMount,
    netIncomeAfterExpense: netIncomeAfterExpense,
    taxTotal: taxTotal,
    incomeTotal: incomeTotal,
    summaryTaxTotal: summaryTaxTotal,
  };
}
